import { FC, useEffect, useLayoutEffect } from "react";
import { Loader2 } from "lucide-react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useOrganizationStore } from "../store/useOrganizationStore";

interface OrganizationBootstrapProps {
  children: React.ReactNode;
}

export const OrganizationBootstrap: FC<OrganizationBootstrapProps> = ({
  children,
}) => {
  const { authStatus } = useAuthenticator();
  const fetchOrganizations = useOrganizationStore(
    (state) => state.fetchOrganizations,
  );
  const setOrgBootstrapPending = useOrganizationStore(
    (state) => state.setOrgBootstrapPending,
  );
  const isOrgBootstrapPending = useOrganizationStore(
    (state) => state.isOrgBootstrapPending,
  );
  const organizationsLength = useOrganizationStore(
    (state) => state.organizations.length,
  );

  useLayoutEffect(() => {
    // Skip the pending overlay when orgs were already seeded by hydrateFromLogin.
    if (authStatus === "authenticated" && organizationsLength === 0) {
      setOrgBootstrapPending(true);
    } else {
      setOrgBootstrapPending(false);
    }
  }, [authStatus, organizationsLength, setOrgBootstrapPending]);

  useEffect(() => {
    // Skip the network round-trip when orgs were already seeded by hydrateFromLogin.
    if (authStatus === "authenticated" && organizationsLength === 0) {
      fetchOrganizations();
    }
  }, [authStatus, fetchOrganizations, organizationsLength]);

  if (authStatus === "authenticated" && isOrgBootstrapPending) {
    return (
      <div
        className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/60 backdrop-blur-sm"
        aria-busy="true"
        aria-live="polite"
        aria-label="Loading organization"
        style={{ pointerEvents: "auto" }}
      >
        <div className="flex flex-col items-center gap-3 rounded-xl bg-slate-900/95 px-6 py-5 shadow-xl ring-1 ring-white/10">
          <Loader2
            className="h-10 w-10 animate-spin text-slate-300"
            aria-hidden
          />
          <span className="text-sm font-medium text-slate-300">
            Loading…
          </span>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
